import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { readSupportedTenantSession } from './supportedTenantsSession';
import { canSeeAskSbIntegrations } from './askSbNav';
import {
  connectDrive,
  deleteOpenAiKey,
  disconnectDrive,
  getAskSbIntegration,
  listAskSbTenantUsers,
  openAskSbOriginal,
  putAskSbGrant,
  putAskSbIntegration,
  storeOpenAiKey,
  testDrive,
  testOpenAi,
} from '../../services/askSb/askSbApi';

const SUPPORTED_MODELS = ['gpt-4.1', 'gpt-4.1-mini', 'gpt-4o'];
const RECOMMENDED_MODEL = 'gpt-4.1';
const OPENAI_KEYS_URL = 'https://platform.openai.com/api-keys';
const cardPadding = { p: 1.5, '&:last-child': { pb: 1.5 } };

const STATUS_LABEL = {
  not_configured: 'Not configured',
  awaiting_authorization: 'Awaiting authorization',
  connected: 'Connected',
  expired: 'Expired',
  disabled: 'Disabled',
  unavailable: 'Unavailable',
};

function statusLabel(status) {
  return STATUS_LABEL[status] || 'Not configured';
}

function userIdOf(user) {
  return user?.uid || user?.id || user?.userId || '';
}

function userLabelOf(user) {
  return user?.displayName || user?.name || user?.email || userIdOf(user);
}

function emptyIntegration() {
  return {
    drive: {
      enabled: false,
      status: 'not_configured',
      accountEmail: null,
      rootFolderId: '',
      rootFolderName: '',
      includeSubfolders: false,
      scopeConfirmed: false,
    },
    openai: {
      enabled: false,
      status: 'not_configured',
      model: '',
      adminInstructions: '',
      secretRef: null,
    },
    grants: {},
  };
}

export default function AskSbIntegrationsPage() {
  const { role, userClaims, userData, tenantId } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const supportSession = readSupportedTenantSession();
  const resolvedRole = role || userClaims?.role || userData?.role || '';
  const allowed = canSeeAskSbIntegrations({
    role: resolvedRole,
    dealerShell: Boolean(dealerAdmin?.active),
    supportShell: Boolean(supportSession?.tenantId),
    operatorNav: resolvedRole === 'operator',
  });

  const [integration, setIntegration] = useState(emptyIntegration);
  const [folderId, setFolderId] = useState('');
  const [includeSubfolders, setIncludeSubfolders] = useState(false);
  const [scopeConfirmed, setScopeConfirmed] = useState(false);
  const [model, setModel] = useState('');
  const [adminInstructions, setAdminInstructions] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [driveBusy, setDriveBusy] = useState(false);
  const [openAiBusy, setOpenAiBusy] = useState(false);

  function applyIntegration(data) {
    const next = {
      ...emptyIntegration(),
      ...data,
      drive: { ...emptyIntegration().drive, ...(data?.drive || {}) },
      openai: { ...emptyIntegration().openai, ...(data?.openai || {}) },
      grants: data?.grants || {},
    };
    setIntegration(next);
    setFolderId(next.drive.rootFolderId || '');
    setIncludeSubfolders(Boolean(next.drive.includeSubfolders));
    setScopeConfirmed(Boolean(next.drive.scopeConfirmed));
    setModel(next.openai.model || RECOMMENDED_MODEL);
    setAdminInstructions(next.openai.adminInstructions || '');
    setApiKey('');
  }

  async function reload() {
    const next = await getAskSbIntegration();
    applyIntegration(next);
    try {
      const members = await listAskSbTenantUsers(tenantId);
      setUsers(Array.isArray(members) ? members : []);
    } catch (err) {
      setUsers([]);
      setError(err?.message || 'Could not load users');
    }
  }

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        await reload();
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Integrations could not be loaded');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, tenantId]);

  function payload(overrides = {}) {
    return {
      rootFolderId: folderId.trim() || null,
      includeSubfolders: Boolean(includeSubfolders),
      scopeConfirmed: Boolean(scopeConfirmed),
      adminInstructions: adminInstructions.slice(0, 4000),
      model: model.trim() || null,
      driveEnabled: Boolean(integration.drive?.enabled),
      openaiEnabled: Boolean(integration.openai?.enabled),
      ...overrides,
    };
  }

  async function saveFolder() {
    if (!scopeConfirmed || !folderId.trim()) return;
    setDriveBusy(true);
    setError('');
    setNotice('');
    try {
      await putAskSbIntegration(payload());
      await reload();
      setNotice('Folder saved.');
    } catch (err) {
      setError(err?.message || 'Could not save the folder');
    } finally {
      setDriveBusy(false);
    }
  }

  async function runDrive(action) {
    setDriveBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await action();
      if (result?.authorizationUrl) {
        await openAskSbOriginal(result.authorizationUrl);
      }
      if (action === disconnectDrive) {
        setIntegration((current) => ({
          ...current,
          drive: {
            ...current.drive,
            enabled: false,
            status: 'disabled',
            accountEmail: null,
          },
        }));
      }
      await reload();
      if (result?.testedCapability) setNotice(result.testedCapability);
    } catch (err) {
      setError(err?.message || 'Drive request failed');
    } finally {
      setDriveBusy(false);
    }
  }

  async function saveOpenAiSettings() {
    setOpenAiBusy(true);
    setError('');
    setNotice('');
    try {
      await putAskSbIntegration(payload());
      await reload();
      setNotice('OpenAI settings saved.');
    } catch (err) {
      setError(err?.message || 'Could not save OpenAI settings');
    } finally {
      setOpenAiBusy(false);
    }
  }

  async function replaceKey() {
    const next = apiKey.trim();
    if (!next) return;
    setOpenAiBusy(true);
    setError('');
    setNotice('');
    try {
      await storeOpenAiKey(next);
      setApiKey('');
      await reload();
      setNotice('API key stored.');
    } catch (err) {
      setError(err?.message || 'Could not store the API key');
    } finally {
      setOpenAiBusy(false);
    }
  }

  async function runOpenAi(action) {
    setOpenAiBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await action();
      if (action === deleteOpenAiKey) {
        setIntegration((current) => ({
          ...current,
          openai: {
            ...current.openai,
            enabled: false,
            status: 'disabled',
            secretRef: null,
          },
        }));
        setApiKey('');
      }
      await reload();
      if (result?.testedCapability) setNotice(result.testedCapability);
    } catch (err) {
      setError(err?.message || 'OpenAI request failed');
    } finally {
      setOpenAiBusy(false);
    }
  }

  async function setGrant(userId, granted) {
    setError('');
    try {
      await putAskSbGrant(userId, granted);
      await reload();
    } catch (err) {
      setError(err?.message || 'Could not update the grant');
    }
  }

  if (!allowed) {
    return (
      <MainLayout scrollMain>
        <Box sx={{ p: 2 }} data-testid="ask-sb-integrations-denied">
          <Typography variant="h5">Integrations</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Ask Signal Bridges integrations are available to tenant admins.
          </Typography>
        </Box>
      </MainLayout>
    );
  }

  const drive = integration.drive || {};
  const openai = integration.openai || {};
  const keyStored = openai.keyStored === true || Boolean(openai.secretRef) || openai.status === 'connected';
  const modelChoices = SUPPORTED_MODELS.includes(model) ? SUPPORTED_MODELS : [model, ...SUPPORTED_MODELS];

  return (
    <MainLayout scrollMain>
      <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1.5, pb: 3, maxWidth: 720 }} data-testid="ask-sb-integrations-page">
        <Typography variant="h5" component="h1" sx={{ fontWeight: 650 }}>
          Integrations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          Connect Google Drive, confirm the policy folder, then save model gpt-4.1 and the API key.
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }} data-testid="ask-sb-integrations-error">{error}</Alert> : null}
        {notice ? <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert> : null}
        {loading ? <Typography sx={{ mb: 2 }}>Loading integrations…</Typography> : null}

        <Stack spacing={1.25}>
          <Card data-testid="ask-sb-drive-card" variant="outlined">
            <CardContent sx={cardPadding}>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 650 }}>1. Google Drive</Typography>
              <Typography variant="body2" data-testid="ask-sb-drive-status" sx={{ mt: 0.5 }}>
                {statusLabel(drive.status)}
              </Typography>
              {drive.accountEmail ? (
                <Typography variant="body2" color="text.secondary">{drive.accountEmail}</Typography>
              ) : null}
              {drive.rootFolderName ? (
                <Typography variant="body2" color="text.secondary">{drive.rootFolderName}</Typography>
              ) : null}
              {drive.testedAt || drive.testedCapability ? (
                <Typography variant="caption" display="block" color="text.secondary">
                  {[drive.testedCapability, drive.testedAt].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ mt: 1 }}>
                Paste the folder link from Google Drive, or the folder id.
              </Typography>
              <TextField
                fullWidth
                size="small"
                sx={{ mt: 0.75 }}
                label="Folder link or id"
                value={folderId}
                onChange={(event) => {
                  setFolderId(event.target.value);
                  setScopeConfirmed(false);
                }}
              />
              <FormControlLabel
                sx={{ mt: 0.5, mb: 0, alignItems: 'flex-start' }}
                control={(
                  <Checkbox
                    checked={includeSubfolders}
                    onChange={(event) => setIncludeSubfolders(event.target.checked)}
                  />
                )}
                label="Include subfolders"
              />
              <FormControlLabel
                sx={{ mt: 0, mb: 0, alignItems: 'flex-start' }}
                control={(
                  <Checkbox
                    checked={scopeConfirmed}
                    onChange={(event) => setScopeConfirmed(event.target.checked)}
                    inputProps={{ 'aria-label': 'Confirm folder scope' }}
                  />
                )}
                label="I confirm documents in this folder may be made available through Ask Signal Bridges to granted users."
              />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={driveBusy || !scopeConfirmed || !folderId.trim()}
                  onClick={saveFolder}
                >
                  Save folder
                </Button>
                <Button size="small" disabled={driveBusy} onClick={() => runDrive(connectDrive)}>Connect</Button>
                <Button size="small" disabled={driveBusy} onClick={() => runDrive(testDrive)}>Test</Button>
                <Button size="small" disabled={driveBusy} color="warning" onClick={() => runDrive(disconnectDrive)}>
                  Disconnect
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card data-testid="ask-sb-openai-card" variant="outlined">
            <CardContent sx={cardPadding}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 650 }}>2. OpenAI</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={OPENAI_KEYS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get API key
                </Button>
              </Stack>
              <Typography variant="body2" data-testid="ask-sb-openai-status" sx={{ mt: 0.5 }}>
                {statusLabel(openai.status)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {keyStored ? 'A key is stored.' : 'No key stored.'}
              </Typography>
              {openai.testedAt || openai.testedCapability ? (
                <Typography variant="caption" display="block" color="text.secondary">
                  {[openai.testedCapability, openai.testedAt].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
              <TextField
                select
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                label="Model"
                value={model}
                onChange={(event) => setModel(event.target.value)}
                helperText="Use gpt-4.1."
              >
                {modelChoices.filter(Boolean).map((choice) => (
                  <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                size="small"
                type="password"
                sx={{ mt: 1 }}
                label="API key"
                autoComplete="new-password"
                placeholder="Paste the key here"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                helperText="The stored key is never shown. Submitting a new key replaces it."
              />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                <Button size="small" variant="contained" disabled={openAiBusy} onClick={saveOpenAiSettings}>
                  Save settings
                </Button>
                <Button size="small" disabled={openAiBusy || !apiKey.trim()} onClick={replaceKey}>
                  Save API key
                </Button>
                <Button size="small" disabled={openAiBusy} onClick={() => runOpenAi(testOpenAi)}>Test</Button>
                <Button size="small" disabled={openAiBusy} color="warning" onClick={() => runOpenAi(deleteOpenAiKey)}>
                  Disconnect
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" data-testid="ask-sb-grants">
            <CardContent sx={cardPadding}>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 650 }}>3. Grants</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                Grant or revoke Ask Signal Bridges for people in this tenant.
              </Typography>
              {users.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No users loaded.</Typography>
              ) : users.map((user) => {
                const id = userIdOf(user);
                const granted = Boolean(integration.grants?.[id]?.granted);
                return (
                  <Stack
                    key={id || userLabelOf(user)}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ py: 0.5, gap: 1 }}
                  >
                    <Typography variant="body2">{userLabelOf(user)}</Typography>
                    <Button size="small" onClick={() => setGrant(id, !granted)}>
                      {granted ? 'Revoke' : 'Grant'}
                    </Button>
                  </Stack>
                );
              })}
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent sx={cardPadding}>
              <Box component="details" data-testid="ask-sb-technical-details">
                <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 650 }}>
                  Technical details
                </Box>
                <Alert severity="warning" sx={{ mt: 2 }} data-testid="ask-sb-scope-warning">
                  The Google scope drive.readonly is not limited to the selected folder.
                  Google does not restrict this token to that folder. Signal Bridge enforces the approved folder.
                </Alert>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Leave Include subfolders off to use only files directly in the approved folder.
                  Turn it on to include subfolders. Signal Bridge still limits how far it walks.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  A grant lets a person ask through Signal Bridge. It is not that person&apos;s own Drive login.
                  Signal Bridge reads the approved folder and sends the needed document text to this tenant&apos;s OpenAI service.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  sx={{ mt: 2 }}
                  label="Admin instructions"
                  value={adminInstructions}
                  onChange={(event) => setAdminInstructions(event.target.value.slice(0, 4000))}
                  inputProps={{ maxLength: 4000 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </MainLayout>
  );
}
